import { formatNumber } from '@progress/kendo-intl'
import { Popup } from '@progress/kendo-react-popup'
import { Tooltip } from '@progress/kendo-react-tooltip'
import classNames from 'classnames'
import { useSetAtom } from 'jotai'
import React from 'react'
import { useHover } from 'react-use'

import { Icon } from '~/components/elements/Icon'
import { type CellInfo, exists } from '~/utils'

import { columnWidthMapAtom } from '../atoms'
import { insertLineBreakEveryNumberOfCharacters } from '../utils'

import styles from './Cell.css'

export type CellProps = {
  cellInfo: CellInfo
  columnIndex: number
  disabled?: boolean
  // スペーサー列は左隣のセルを描画しないため、このセル自身が左罫線を描く
  isAfterSpacerColumn?: boolean
  left?: number
  rowIndex: number
  textLineCount: number
}

export const Cell = ({
  cellInfo,
  cellInfo: {
    align = 'right',
    className,
    colSpan = 1,
    group,
    heatmap,
    help,
    icon,
    isBorderShownOnHoverWhenClickable,
    isColumnHeader,
    isHeader,
    isHighlighted,
    node,
    onClick,
    popup,
    ratio,
    rowSpan = 1,
    text,
    textCharacterLimit,
    textOrientation,
    textWritingMode,
  },
  columnIndex,
  disabled,
  isAfterSpacerColumn = false,
  left,
  rowIndex,
  textLineCount,
}: CellProps) => {
  const ref = React.useRef<HTMLDivElement>(null)

  const setColumnWidthMap = useSetAtom(columnWidthMapAtom)

  const clickable = React.useMemo(
    () => exists(onClick) && !disabled,
    [disabled, onClick],
  )

  const gridArea = React.useMemo<React.CSSProperties['gridArea']>(() => {
    const gridRowStart = rowIndex + 1
    const gridColumnStart = columnIndex + 1
    const gridRowEnd = gridRowStart + rowSpan
    const gridColumnEnd = gridColumnStart + colSpan

    return [gridRowStart, gridColumnStart, gridRowEnd, gridColumnEnd].join(
      ' / ',
    )
  }, [colSpan, rowSpan, columnIndex, rowIndex])

  React.useEffect(() => {
    if (colSpan !== 1) {
      return
    }

    const width = ref.current?.getBoundingClientRect().width
    if (exists(width)) {
      setColumnWidthMap((columnWidthMap) => ({
        ...columnWidthMap,
        [columnIndex]: width,
      }))
    }
  }, [cellInfo, colSpan, columnIndex, setColumnWidthMap])

  const [element] = useHover((hovered) => (
    <div
      ref={ref}
      className={classNames(
        styles.cell,
        styles[`cell-align-${align}`],
        {
          [styles.cellNotFirstRow]: rowIndex !== 0,
          [styles.cellNotFirstColumn]:
            columnIndex !== 0 && !isAfterSpacerColumn,
          [styles.cellHeader]: isHeader,
          [styles.cellColumnHeader]: isColumnHeader,
          [styles.cellClickable]: clickable,
          [styles[`cell-${group}`]]: exists(group),
        },
        className,
      )}
      style={{
        gridArea,
        left,
        position: exists(left) ? 'sticky' : undefined,
        zIndex: exists(left) ? 1 : 0,
      }}
      onClick={clickable ? onClick : undefined}
    >
      {clickable && isBorderShownOnHoverWhenClickable && (
        <div className={styles.border} />
      )}

      {exists(ratio) &&
        (heatmap ? (
          <span
            className={styles.heatmap}
            style={{
              // positive: #57bb8a
              // neutral: #ffffff;
              // negative: #e67c73
              backgroundColor:
                ratio > 0
                  ? `rgb(
                  ${255 - (255 - 87) * ratio},
                  ${255 - (255 - 187) * ratio},
                  ${255 - (255 - 138) * ratio}
                  )`
                  : `rgb(
                  ${255 - (255 - 230) * ratio * -1},
                  ${255 - (255 - 124) * ratio * -1},
                  ${255 - (255 - 115) * ratio * -1}
                  )`,
            }}
          />
        ) : (
          <span
            className={styles.bar}
            style={{ width: formatNumber(Math.min(ratio, 1), 'p3') }}
          />
        ))}

      {isHighlighted && <div className={styles.borderHighlighted} />}

      <div
        className={classNames(
          classNames(styles.contents, {
            [styles.contentsWithIcon]: exists(icon),
            [styles.contentsIcon]: exists(icon) && icon !== 'blank',
            [styles[`contentsIcon-${icon}`]]: exists(icon) && icon !== 'blank',
          }),
        )}
      >
        {exists(text) && (
          <span
            className={styles.text}
            style={{
              ...(exists(textCharacterLimit) && {
                minHeight: `${textLineCount}lh`,
                textAlign: 'end',
              }),
              ...(exists(textWritingMode) || exists(textOrientation)
                ? {
                    display: 'block',
                    writingMode: textWritingMode,
                    textOrientation,
                  }
                : {}),
            }}
          >
            {exists(textCharacterLimit) &&
            textCharacterLimit * textLineCount < `${text}`.length ? (
              <Tooltip openDelay={0} position="left">
                <span title={`${text}`}>
                  {`${insertLineBreakEveryNumberOfCharacters(text, textCharacterLimit)}`.slice(
                    0,
                    textCharacterLimit * textLineCount - 1,
                  )}
                  ...
                </span>
              </Tooltip>
            ) : exists(textCharacterLimit) &&
              textCharacterLimit < `${text}`.length ? (
              insertLineBreakEveryNumberOfCharacters(text, textCharacterLimit)
            ) : (
              text
            )}
          </span>
        )}

        {exists(node) && node}

        {exists(help) && (
          <Tooltip anchorElement="target" openDelay={0} position="left">
            <Icon icon="help" size="1rem" title={help} />
          </Tooltip>
        )}
      </div>

      {popup && (
        <Popup
          anchor={ref.current}
          anchorAlign={{ horizontal: 'right', vertical: 'top' }}
          animate={false}
          popupAlign={{ horizontal: 'right', vertical: 'bottom' }}
          show={hovered}
        >
          {popup}
        </Popup>
      )}
    </div>
  ))

  return element
}
